import React from "react";
import { Box, Typography } from "@mui/material";
import MicNoneIcon from "@mui/icons-material/MicNone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardAltOutlinedIcon from "@mui/icons-material/KeyboardAltOutlined";
import ChatbotIcon from "../../ChatbotIcon";
import { primaryColor } from "../../../Theme";

type GuideStep = {
  label: string;
  text: string;
  sample?: boolean;
};

type GuideScenario = {
  title: string;
  steps: GuideStep[];
};

const inputModes = [
  { title: "Voice", hint: "Speak, then send", Icon: MicNoneIcon },
  { title: "Transcript", hint: "Upload .vtt, then send", Icon: DescriptionOutlinedIcon },
  { title: "Type", hint: "Type, then send", Icon: KeyboardAltOutlinedIcon },
] as const;

const scenarios: GuideScenario[] = [
  {
    title: "Single-step prescribe",
    steps: [
      {
        label: "Prompt",
        text: "Prescribe abilify 2 mg. Take one tablet in the morning and one in the evening for 10 days",
        sample: true,
      },
      { label: "Action", text: "Confirm" },
    ],
  },
  {
    title: "Refine the order",
    steps: [
      { label: "Prompt", text: "Prescribe Lisinopril oral tablet", sample: true },
      { label: "Select", text: "Lisinopril 10 MG Oral Tablet" },
      { label: "Quantity", text: "20 tablets" },
      { label: "Action", text: "Send to Pharmacy" },
    ],
  },
  {
    title: "Edit and send",
    steps: [
      {
        label: "Prompt",
        text: "Lisinopril 30 mg tablet daily once after dinner. The prescription is for 30 days",
        sample: true,
      },
      { label: "Update", text: "update quantity to 15 tablets", sample: true },
      { label: "Action", text: "Send it" },
    ],
  },
];

const Scenario: React.FC<GuideScenario & { index: number }> = ({ title, steps, index }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      minWidth: 0,
      height: "100%",
      border: `1px solid ${primaryColor}1a`,
      borderRadius: "10px",
      overflow: "hidden",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        px: 1,
        py: 0.5,
        backgroundColor: `${primaryColor}0c`,
        color: primaryColor,
        display: "flex",
        alignItems: "center",
        gap: 0.6,
        flex: "none",
      }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: primaryColor,
          color: "#fff",
          fontSize: "0.6rem",
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Box sx={{ fontSize: "0.72rem", fontWeight: 700, lineHeight: 1.2 }}>{title}</Box>
    </Box>
    <Box
      sx={{
        px: 1,
        py: 0.6,
        display: "flex",
        flexDirection: "column",
        gap: 0.45,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      {steps.map((step) => (
        <Box key={`${step.label}-${step.text}`} sx={{ minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: primaryColor,
              mr: 0.4,
            }}
          >
            {step.label}:
          </Box>
          {step.sample ? (
            <Box
              component="span"
              sx={{
                fontSize: "0.7rem",
                lineHeight: 1.3,
                color: "text.primary",
                fontStyle: "italic",
              }}
            >
              “{step.text}”
            </Box>
          ) : (
            <Box component="span" sx={{ fontSize: "0.7rem", lineHeight: 1.3, fontWeight: 600 }}>
              {step.text}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  </Box>
);

/** Empty-state walkthrough shown when AgenticRx is enabled for the account. */
const AgenticRxQuickGuide: React.FC = () => (
  <Box
    sx={{
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      minHeight: 0,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      overflow: { xs: "auto", md: "hidden" },
      px: { xs: 0.5, sm: 1 },
      gap: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "none", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: "50%",
          flex: "none",
          background: `radial-gradient(circle at 32% 28%, ${primaryColor}ee, ${primaryColor})`,
          boxShadow: `0 4px 10px ${primaryColor}28`,
        }}
      >
        <ChatbotIcon color="#ffffff" style={{ width: 16, height: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          sx={{ color: primaryColor, fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}
        >
          AgenticRx Guide
        </Typography>
        <Typography component="div" sx={{ color: `${primaryColor}aa`, fontSize: "0.72rem" }}>
          Start in the bar below, then try an example
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${primaryColor}22`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: `${primaryColor}06`,
      }}
    >
      <Box
        sx={{
          flex: "none",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr auto 1fr" },
          alignItems: "stretch",
          columnGap: { xs: 0.75, sm: 1 },
          rowGap: 0.6,
          px: { xs: 0.85, sm: 1.1 },
          py: 0.75,
          borderBottom: `1px solid ${primaryColor}18`,
          backgroundColor: "#fff",
        }}
      >
        {inputModes
          .flatMap(({ title, hint, Icon }, index) => [
            index > 0 ? (
              <Box
                key={`or-${title}`}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: `${primaryColor}77`,
                  textTransform: "uppercase",
                  px: 0.25,
                }}
              >
                or
              </Box>
            ) : null,
            <Box
              key={title}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                width: "100%",
                px: { xs: 1.1, sm: 1.35 },
                py: 0.55,
                borderRadius: "10px",
                backgroundColor: `${primaryColor}10`,
                color: primaryColor,
                boxSizing: "border-box",
              }}
              title={hint}
            >
              <Icon sx={{ fontSize: 18, flex: "none" }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ fontSize: "0.78rem", fontWeight: 700, lineHeight: 1.15 }}>{title}</Box>
                <Box
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    color: `${primaryColor}aa`,
                    lineHeight: 1.2,
                  }}
                >
                  {hint}
                </Box>
              </Box>
            </Box>,
          ])
          .filter(Boolean)}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
          gridTemplateRows: { xs: "auto", sm: "minmax(0, 1fr)" },
          gap: { xs: 0.7, sm: 0.85 },
          p: { xs: 0.75, sm: 0.9 },
        }}
      >
        {scenarios.map((scenario, index) => (
          <Scenario key={scenario.title} index={index + 1} title={scenario.title} steps={scenario.steps} />
        ))}
      </Box>
    </Box>
  </Box>
);

export default AgenticRxQuickGuide;
